import { PILL_CARET_GUARD } from "./pill-caret-guards";

/**
 * Whole-pill selection helpers for the PandaDoc-style two-click interaction on
 * variable pills: the first click SELECTS the pill (it is a
 * `contenteditable="false"` atom, so the selection is the whole thing or
 * nothing), the second click on an already-selected pill opens its popover.
 * See Froala.tsx's pill mousedown handling.
 */

const stripGuards = (text: string): string =>
  text.split(PILL_CARET_GUARD).join("");

/**
 * True when the live selection covers exactly `pill` — nothing more, nothing
 * less. Compared by selected TEXT rather than by range endpoints, because
 * browsers report a click-selected atom in several shapes (`(parent,i)→
 * (parent,i+1)`, `(pill,0)→(pill,n)`, or text-node offsets), and the ZWS caret
 * guards around a pill may or may not be swept in.
 */
export const isWholePillSelected = (
  pill: Element,
  sel: Selection | null,
): boolean => {
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  if (!range.intersectsNode(pill)) return false;
  const selected = stripGuards(range.toString());
  const own = stripGuards(pill.textContent ?? "");
  return own.length > 0 && selected === own;
};

/** Select exactly `pill` (as a range in its parent, so no endpoint sits inside
 * the atom). Returns false when the pill is detached. */
export const selectWholePill = (
  pill: Element,
  sel: Selection | null = pill.ownerDocument.getSelection(),
): boolean => {
  const parent = pill.parentNode;
  if (!sel || !parent) return false;
  const index = Array.prototype.indexOf.call(parent.childNodes, pill);
  if (index < 0) return false;
  const range = pill.ownerDocument.createRange();
  range.setStart(parent, index);
  range.setEnd(parent, index + 1);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};
